import {
  ButtonProps,
  Card,
  CardActionArea,
  Stack,
  SxProps,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'

import {
  CollectionBoxFragment,
  UserCollectionBoxFragment,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter } from '@/core'
import { Organism } from '@/core/architecture'

import { CollectionIndicator } from '../CollectionIndicator'

export type CollectionBoxProps = {
  collection: CollectionBoxFragment | UserCollectionBoxFragment
  fullWidth?: boolean
  userId?: string
  sx?: SxProps & { height?: number | string }
  actionAreaProps?: {
    href?: ButtonProps['href']
    onClick?: ButtonProps['onClick']
  }
}

export const CollectionBox: Organism<CollectionBoxProps> = ({
  collection,
  sx,
  fullWidth,
  userId,
  actionAreaProps,
}) => {
  const { routerPages, generatePath } = useRouter<{ collectionId: string }>()

  const { collection: currentCollection, userCollection } = useMemo(() => {
    if (isCollection(collection)) {
      return {
        collection: collection,
        userCollection: collection.userCollection,
      }
    }
    return {
      collection: collection.collection,
      userCollection: collection,
    }
  }, [collection])

  const thumbnailUri =
    currentCollection?.thumbnail?.mediumUri ??
    currentCollection?.thumbnail?.uri ??
    ''

  return (
    <Card
      sx={{
        width: fullWidth ? '100%' : 315,
        height: sx && 'height' in sx ? sx.height : 190,
        borderRadius: 2,
        ...sx,
      }}
    >
      <CardActionArea
        sx={{ height: '100%' }}
        href={
          userId
            ? generatePath(routerPages.UserCollectionDetail, {
                collectionId: currentCollection?.id ?? '#',
                userId,
              })
            : generatePath(routerPages.CollectionDetail, {
                collectionId: currentCollection?.id ?? '#',
              })
        }
        {...actionAreaProps}
      >
        <Stack
          direction="column-reverse"
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `url("${thumbnailUri}")`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <Stack
            justifyContent="flex-end"
            sx={{
              ...styleSets.backgroundImageWithGradient(),
              mt: 4,
              width: '100%',
              height: 56,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mx: 4, pt: 1, pb: userCollection ? 0 : 4 }}
            >
              {currentCollection?.title}
            </Typography>
            {/* ユーザーがログインしている場合、プログレスバーを表示 */}
            {userCollection && (
              <CollectionIndicator
                itemCount={userCollection?.totalOwnedItemCount ?? 0}
                totalCount={currentCollection?.itemCount ?? 1}
              />
            )}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  )
}

function isCollection(
  collection: CollectionBoxFragment | UserCollectionBoxFragment
): collection is CollectionBoxFragment {
  return 'title' in collection
}
